import { Shield } from "lucide-react";
import { SEOHead } from "../components/SEOHead";

const SECTIONS = [
  {
    title: "1. Who We Are",
    content: `No Fuss Websites (ABN: 57 371 480 477) is an Australian web design and development agency based in Melbourne, Victoria. We build professional websites for small and medium businesses across Australia.

Contact: hello@nofusswebsites.com.au | 0466 476 264`
  },
  {
    title: "2. Information We Collect",
    content: `We collect personal information when you:
• Fill out our contact or quote request form (name, email, phone number, business name, message)
• Purchase a package or service (name, email, billing details)
• Use our website chat assistant (conversation content)
• Contact us directly via email or phone

We do not collect sensitive information such as health, financial account, or government identifier data beyond what is necessary to process payments via our secure payment provider (Stripe).`
  },
  {
    title: "3. How We Use Your Information",
    content: `We use your personal information to:
• Respond to your enquiries and quote requests
• Process and deliver your website project
• Send you project updates and support communications
• Improve our services and website
• Comply with our legal obligations

We do not sell, trade, or rent your personal information to third parties. We will not send you unsolicited marketing emails without your consent.`
  },
  {
    title: "4. Disclosure to Third Parties",
    content: `We may share your information with trusted third-party service providers who assist us in operating our business, including:
• Stripe — payment processing
• Google Workspace — email and communication
• Resend — transactional email delivery

These providers are bound by their own privacy policies and are not permitted to use your data for any other purpose.`
  },
  {
    title: "5. Cookies & Analytics",
    content: `Our website may use cookies and analytics tools (including Google Analytics) to understand how visitors use our site. This data is aggregated and anonymous. You can disable cookies in your browser settings at any time.

We do not use cookies to track you across other websites or for targeted advertising.`
  },
  {
    title: "6. Data Security",
    content: `We take reasonable steps to protect your personal information from misuse, loss, unauthorised access, modification, or disclosure. Payment information is handled entirely by Stripe and is never stored on our servers.

While we take security seriously, no method of transmission over the internet is 100% secure. We encourage you to use secure networks when submitting personal information.`
  },
  {
    title: "7. Access & Correction",
    content: `You have the right to access and correct the personal information we hold about you. To make a request, contact us at hello@nofusswebsites.com.au. We will respond within 30 days.

If you believe we have breached the Australian Privacy Principles, you may make a complaint to the Office of the Australian Information Commissioner (OAIC) at www.oaic.gov.au.`
  },
  {
    title: "8. Retention",
    content: `We retain your personal information for as long as necessary to fulfil the purposes outlined in this policy, or as required by Australian law. Project records are typically retained for 7 years for accounting and legal purposes.`
  },
  {
    title: "9. Children's Privacy",
    content: `Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us and we will delete it promptly.`
  },
  {
    title: "10. Changes to This Policy",
    content: `We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date at the top of this page. We encourage you to review this policy periodically.`
  },
  {
    title: "11. Contact Us",
    content: `For any privacy-related questions or concerns, please contact us:

Email: hello@nofusswebsites.com.au
Phone: 0466 476 264
Address: Melbourne, Victoria, Australia`
  }
];

export default function PrivacyPolicyPage() {
  return (
    <main className="pt-16" data-testid="privacy-policy-page">
      <SEOHead title="Privacy Policy" description="No Fuss Websites privacy policy. How we collect, use, and protect your personal information." path="/privacy-policy" />
      {/* Hero */}
      <section className="bg-black text-white border-b-4 border-[#FFD600] py-16">
        <div className="container-main">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#FFD600] rounded-xl flex items-center justify-center">
              <Shield size={20} className="text-black" />
            </div>
            <span className="text-[#FFD600] font-bold uppercase tracking-widest text-sm">Legal</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Privacy Policy
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl">
            Your privacy matters to us. This policy explains how No Fuss Websites collects, uses, and protects your personal information in accordance with the <em>Privacy Act 1988</em> (Cth) and the Australian Privacy Principles.
          </p>
          <p className="text-gray-500 text-sm mt-4">Last updated: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container-main max-w-4xl">
          <div className="space-y-10">
            {SECTIONS.map((s) => (
              <div key={s.title} className="border-l-4 border-[#FFD600] pl-6">
                <h2 className="text-xl font-black text-black mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>{s.title}</h2>
                <div className="text-gray-700 leading-relaxed whitespace-pre-line text-sm md:text-base">{s.content}</div>
              </div>
            ))}
          </div>

          {/* Footer note */}
          <div className="mt-16 p-6 bg-[#F4F4F5] border-2 border-black rounded-2xl">
            <p className="text-sm text-gray-600 text-center">
              This Privacy Policy applies to No Fuss Websites (ABN: 57 371 480 477), Melbourne, Victoria, Australia.
              For questions, email <a href="mailto:hello@nofusswebsites.com.au" className="text-[#0055FF] font-semibold hover:underline">hello@nofusswebsites.com.au</a>.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
